/**
 * Exercises API Client
 * Handles exercise library, plans, and session logging
 */

import { apiClient } from '../api-client';

// === TYPES ===

export interface Exercise {
  id: number;
  name: string;
  category: string;
  difficulty: string;
  description: string | null;
  instructions: string | null;
  duration_minutes: number | null;
  sets: number | null;
  reps: number | null;
  video_url: string | null;
  image_url: string | null;
  contraindications: string | null;
}

export interface ExercisePlan {
  id: number;
  patient_id: number;
  exercise_id: number;
  prescribed_by_id: number;
  frequency_per_week: number;
  duration_weeks: number;
  sets: number | null;
  reps: number | null;
  duration_minutes: number | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  exercise?: Exercise;
}

export interface ExerciseSession {
  id: number;
  exercise_plan_id: number;
  patient_id: number;
  session_date: string;
  completed_sets: number | null;
  completed_reps: number | null;
  duration_seconds: number | null;
  difficulty_rating: number | null;
  pain_level: number | null;
  fatigue_level: number | null;
  is_completed: boolean;
  patient_notes: string | null;
  exercise_name?: string;
  exercise_category?: string;
}

export interface ExercisePlanCreate {
  patient_id: number;
  exercise_id: number;
  prescribed_by_id: number;
  frequency_per_week: number;
  duration_weeks: number;
  sets?: number;
  reps?: number;
  duration_minutes?: number;
  notes?: string;
}

export interface ExerciseSessionCreate {
  exercise_plan_id: number;
  patient_id: number;
  completed_sets?: number;
  completed_reps?: number;
  duration_seconds?: number;
  difficulty_rating?: number;
  pain_level?: number;
  fatigue_level?: number;
  is_completed?: boolean;
  patient_notes?: string;
}

export interface ManualExerciseLog {
  patient_id: number;
  exercise_name: string;
  duration_seconds: number;
  intensity?: number; // 1-10
  notes?: string;
  completed_date?: string;
}

export interface CustomExercisePlanCreate {
  patient_id: number;
  plan_title: string;
  exercises: Array<{
    id: string;
    type: string;
    duration: number;
    intensity: string;
    frequency: number;
    progressiveLoad: string;
  }>;
  status: 'active' | 'draft';
  notes?: string;
}

export interface CustomExercisePlanUpdate {
  plan_title?: string;
  exercises?: Array<{
    id: string;
    type: string;
    duration: number;
    intensity: string;
    frequency: number;
    progressiveLoad: string;
  }>;
  status?: 'active' | 'draft';
  notes?: string;
  is_active?: boolean;
}

export interface CustomExercisePlan {
  id: number;
  patient_id: number;
  therapist_id: number;
  plan_title: string;
  exercises: Array<{
    id: string;
    type: string;
    duration: number;
    intensity: string;
    frequency: number;
    progressiveLoad: string;
  }>;
  status: 'active' | 'draft';
  notes?: string;
  created_date: string;
  last_modified: string;
  is_active: boolean;
  plan_type: string;
}

export interface ExerciseStats {
  patient_id: number;
  period_days: number;
  total_sessions: number;
  completed_sessions: number;
  completion_rate: number;
  active_plans: number;
}

// === API FUNCTIONS ===

export const exercisesApi = {
  /**
   * Get exercise library with filters
   */
  getExercises: async (params?: {
    category?: string;
    difficulty?: string;
    search?: string;
    limit?: number;
  }): Promise<{
    exercises: Exercise[];
    count: number;
  }> => {
    return apiClient.get('/exercises', params);
  },

  /**
   * Get exercise by ID
   */
  getExercise: async (exerciseId: number): Promise<Exercise> => {
    return apiClient.get(`/exercises/${exerciseId}`);
  },

  /**
   * Create exercise plan for patient
   */
  createExercisePlan: async (data: ExercisePlanCreate): Promise<{
    message: string;
    plan_id: number;
    exercise_name: string;
  }> => {
    return apiClient.post('/exercises/plans', data);
  },

  /**
   * Get exercise plans for current patient (PATIENT role only)
   * Uses /me endpoint to get plans for authenticated patient
   */
  getMyExercisePlans: async (
    activeOnly?: boolean
  ): Promise<{
    patient_id: number;
    plans: ExercisePlan[];
    count: number;
  }> => {
    return apiClient.get('/exercises/plans/patient/me', {
      active_only: activeOnly,
    });
  },

  /**
   * Get exercise plans for patient (by patient ID)
   * For use by doctors, therapists, coordinators, nurses
   */
  getPatientExercisePlans: async (
    patientId: number,
    activeOnly?: boolean
  ): Promise<{
    patient_id: number;
    plans: ExercisePlan[];
    count: number;
  }> => {
    return apiClient.get(`/exercises/plans/patient/${patientId}`, {
      active_only: activeOnly,
    });
  },

  /**
   * Update exercise plan
   */
  updateExercisePlan: async (
    planId: number,
    data: { is_active?: boolean; notes?: string }
  ): Promise<{
    message: string;
    plan_id: number;
  }> => {
    return apiClient.put(`/exercises/plans/${planId}`, data);
  },

  /**
   * Delete exercise plan
   */
  deleteExercisePlan: async (planId: number): Promise<{
    status: string;
    message: string;
  }> => {
    return apiClient.delete(`/exercises/plans/${planId}`);
  },

  /**
   * Log exercise session
   */
  logExerciseSession: async (data: ExerciseSessionCreate): Promise<{
    message: string;
    session_id: number;
    session_date: string;
  }> => {
    return apiClient.post('/exercises/sessions', data);
  },

  /**
   * Get exercise session history for patient
   */
  getPatientExerciseSessions: async (
    patientId: number,
    params?: { days?: number; limit?: number }
  ): Promise<{
    patient_id: number;
    period_days: number;
    sessions: ExerciseSession[];
    count: number;
  }> => {
    return apiClient.get(`/exercises/sessions/patient/${patientId}`, params);
  },

  /**
   * Update exercise session
   */
  updateExerciseSession: async (
    sessionId: number,
    data: ExerciseSessionCreate
  ): Promise<{
    message: string;
    session_id: number;
    session_date: string;
  }> => {
    return apiClient.put(`/exercises/sessions/${sessionId}`, data);
  },

  /**
   * Delete exercise session
   */
  deleteExerciseSession: async (sessionId: number): Promise<{
    message: string;
    session_id: number;
    session_date: string;
    patient_id: number;
  }> => {
    return apiClient.delete(`/exercises/sessions/${sessionId}`);
  },

  /**
   * Get exercise statistics
   */
  getExerciseStats: async (
    patientId: number,
    days?: number
  ): Promise<ExerciseStats> => {
    return apiClient.get(`/exercises/sessions/stats/${patientId}`, { days });
  },

  /**
   * Log manual exercise (not part of a prescribed plan)
   */
  logManualExercise: async (data: ManualExerciseLog): Promise<{
    status: string;
    message: string;
    session_id: number;
  }> => {
    return apiClient.post('/exercises/manual-log', data);
  },

  /**
   * Create custom exercise plan (multiple exercises, not from library)
   */
  createCustomExercisePlan: async (data: CustomExercisePlanCreate): Promise<{
    message: string;
    plan_id: number;
    patient_id: number;
    exercise_count: number;
  }> => {
    return apiClient.post('/exercises/plans/custom', data);
  },

  /**
   * Update custom exercise plan
   */
  updateCustomExercisePlan: async (
    planId: number,
    data: CustomExercisePlanUpdate
  ): Promise<{
    message: string;
    plan_id: number;
  }> => {
    return apiClient.put(`/exercises/plans/custom/${planId}`, data);
  },

  /**
   * Get custom exercise plans for current patient (PATIENT role only)
   * Uses /me endpoint to get plans for authenticated patient
   */
  getMyCustomExercisePlans: async (
    activeOnly?: boolean
  ): Promise<{
    patient_id: number;
    exercise_plans: CustomExercisePlan[];
    count: number;
  }> => {
    return apiClient.get('/exercises/plans/custom/patient/me', {
      active_only: activeOnly,
    });
  },

  /**
   * Get custom exercise plans for patient (by patient ID)
   * For use by doctors, therapists, coordinators, nurses
   */
  getPatientCustomExercisePlans: async (
    patientId: number,
    activeOnly?: boolean
  ): Promise<{
    patient_id: number;
    exercise_plans: CustomExercisePlan[];
    count: number;
  }> => {
    return apiClient.get(`/exercises/plans/custom/patient/${patientId}`, {
      active_only: activeOnly,
    });
  },
};

// Standalone exports for hooks
export const getExercises = exercisesApi.getExercises;
export const getExercise = exercisesApi.getExercise;
export const createExercisePlan = exercisesApi.createExercisePlan;
export const getPatientExercisePlans = exercisesApi.getPatientExercisePlans;
export const updateExercisePlan = exercisesApi.updateExercisePlan;
export const deleteExercisePlan = exercisesApi.deleteExercisePlan;
export const logExerciseSession = exercisesApi.logExerciseSession;
export const getPatientExerciseSessions = exercisesApi.getPatientExerciseSessions;
export const updateExerciseSession = exercisesApi.updateExerciseSession;
export const deleteExerciseSession = exercisesApi.deleteExerciseSession;
export const getExerciseStats = exercisesApi.getExerciseStats;
export const logManualExercise = exercisesApi.logManualExercise;
export const createCustomExercisePlan = exercisesApi.createCustomExercisePlan;
export const updateCustomExercisePlan = exercisesApi.updateCustomExercisePlan;
export const getPatientCustomExercisePlans = exercisesApi.getPatientCustomExercisePlans;